import React, { useState } from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { X, ShieldCheck, ArrowRight, MessageSquare, Check, User, Phone, MapPin, Zap } from 'lucide-react';

export const InstantQuoteModal: React.FC = () => {
  const { isLeadModalOpen, setIsLeadModalOpen, leadConsultationType, selectedPreset, presets } = useSolarStore();

  const [step, setStep] = useState<1 | 2>(1);
  const [fullName, setFullName] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [state, setState] = useState('Lagos');
  const [selectedPkgId, setSelectedPkgId] = useState(selectedPreset.id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isLeadModalOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !whatsappNumber.trim()) {
      setError('Please provide your full name and WhatsApp phone number.');
      return;
    }
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName,
          whatsappNumber,
          state,
          selectedPresetId: selectedPkgId,
          dailyFuelSpend: 10000,
          consultationType: leadConsultationType,
        }),
      });

      const data = await response.json();
      if (data.success && data.whatsappRedirectUrl) {
        // Redirect directly to WhatsApp with pre-filled message
        window.open(data.whatsappRedirectUrl, '_blank');
        setIsLeadModalOpen(false);
      } else {
        setError('Failed to generate quote. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
      <div className="bg-slate-900 border border-emerald-500/40 rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl text-white relative animate-in fade-in zoom-in duration-200">
        
        {/* Close Button */}
        <button
          onClick={() => setIsLeadModalOpen(false)}
          className="absolute top-6 right-6 text-slate-400 hover:text-white bg-slate-800/80 p-2 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="space-y-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold mb-2">
              <MessageSquare className="w-3.5 h-3.5" /> Access Tech Instant WhatsApp Quote
            </div>
            <h3 className="text-2xl font-black text-white">{leadConsultationType}</h3>
            <p className="text-xs text-slate-400 mt-1">
              Connect directly with our senior COREN engineers on WhatsApp for instant sizing, pricing, and site survey booking.
            </p>
          </div>

          {error && (
            <div className="p-3.5 rounded-xl bg-rose-500/20 border border-rose-500/40 text-rose-300 text-xs font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {step === 1 ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-emerald-400" /> Select Installation State / Location
                  </label>
                  <select
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500"
                  >
                    <option value="Lagos">Lagos State (Lekki, Ikeja, Ikoyi, Ajah...)</option>
                    <option value="Abuja">Abuja (FCT - Maitama, Asokoro, Gwarinpa...)</option>
                    <option value="Port Harcourt">Port Harcourt (Rivers State - GRA, Peter Odili...)</option>
                    <option value="Umuahia">Umuahia / Aba (Abia State)</option>
                    <option value="Ibadan">Ibadan (Oyo State)</option>
                    <option value="Other">Other Nigerian State</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400" /> Select System Package
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {presets.map((pkg) => (
                      <div
                        key={pkg.id}
                        onClick={() => setSelectedPkgId(pkg.id)}
                        className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between text-xs ${
                          selectedPkgId === pkg.id
                            ? 'bg-emerald-950/40 border-emerald-500 text-white font-bold'
                            : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                        }`}
                      >
                        <div>
                          <div className="font-bold text-white">{pkg.name}</div>
                          <div className="text-[10px] text-slate-400">{pkg.capacityKva} kVA • ₦{pkg.priceNgn.toLocaleString()}</div>
                        </div>
                        {selectedPkgId === pkg.id && <Check className="w-4 h-4 text-emerald-400" />}
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3.5 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 text-sm"
                >
                  Proceed to Contact Details
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                    <User className="w-4 h-4 text-emerald-400" /> Full Name
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Dr. Emeka Okoro"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500 placeholder:text-slate-600"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-emerald-400" /> WhatsApp Phone Number (+234)
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="e.g. 08064752074"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500 placeholder:text-slate-600"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="w-1/3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-3.5 rounded-xl transition-all text-xs"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-2/3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-emerald-500/25 transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                  >
                    {isSubmitting ? 'Generating...' : 'Get WhatsApp Quote Now'}
                    <MessageSquare className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </form>

          <div className="pt-4 border-t border-slate-800 text-center">
            <span className="text-[10px] text-slate-500 flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> COREN Certified • Instant WhatsApp Dispatch • No Spam
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
